import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SearchableSelector } from "@/components/ui/searchableSelector";
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
	PaginationEllipsis,
} from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	FileText,
	Search,
	Filter,
	User,
	Users,
	CheckSquare,
	Square,
	X,
	Loader2,
} from "lucide-react";
import type {
	KorrekturStep,
	OrgaAdminUser,
	KorrekturStepStatus,
	StudentKlausurDetailedDTO,
} from "@/lib/orgaAdmin/orgaAdmin.types";
import { getLongestArray } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { useKorrekturStepAssignmentOperations } from "../contexts/korrekturStepAssignment.context";
import {
	KorrekturTableStateProvider,
	useKorrekturTableState,
} from "../contexts/korrekturTableState.context";

/**
 * Provides a consistent set of props for status badges based on the step status.
 * @param status - The status of the korrektur step.
 * @returns An object with className and label for the badge.
 */
const getStatusBadgeProps = (status: KorrekturStepStatus) => {
	switch (status) {
		case "IN_PROGRESS":
			return {
				className: "bg-amber-100 text-amber-800 border-amber-200",
				label: "In Bearbeitung",
			};
		case "COMPLETED":
			return {
				className: "bg-emerald-100 text-emerald-800 border-emerald-200",
				label: "Abgeschlossen",
			};
		case "NOT_STARTED":
			return {
				className: "bg-slate-100 text-slate-600 border-slate-200",
				label: "Nicht begonnen",
			};
		case "LOCKED":
			return {
				className: "bg-orange-100 text-orange-800 border-orange-200",
				label: "Gesperrt",
			};
		default:
			return {
				className: "bg-slate-100 text-slate-600 border-slate-200",
				label: "Unbekannt",
			};
	}
};

/**
 * Hardcoded options for the status filter dropdown.
 */
const statusOptions = [
	{ value: "all", label: "Alle Status" },
	{ value: "NOT_STARTED", label: "Nicht begonnen" },
	{ value: "IN_PROGRESS", label: "In Bearbeitung" },
	{ value: "COMPLETED", label: "Abgeschlossen" },
	{ value: "LOCKED", label: "Gesperrt" },
];

interface StatusCellProps {
	step: KorrekturStep;
	users: OrgaAdminUser[];
}

/**
 * Renders a single cell in the table, displaying the korrektur status and assigned user.
 */
function StatusCell({ step, users }: StatusCellProps) {
	const statusProps = getStatusBadgeProps(step.status);
	const assignedUser = users.find((user) => user.id === step.korrektorId);

	return (
		<div className="flex justify-start">
			{assignedUser ? (
				<Badge
					className={cn(
						"text-xs flex flex-col items-center gap-0.5",
						statusProps.className,
					)}
				>
					<div className="flex font-semibold items-center gap-1 min-w-0">
						<User className="h-3 w-3 flex-shrink-0" />
						<span className="truncate max-w-32">{assignedUser.email}</span>
					</div>
					{step.korrekturStudentKlausur?.lockedMaxPoints > 0 && (
						<span className="text-muted-foreground whitespace-nowrap">
							{step.korrekturStudentKlausur?.lockedTotalPoints} /{" "}
							{step.korrekturStudentKlausur?.lockedMaxPoints} Pkt.
						</span>
					)}
				</Badge>
			) : (
				<Badge className={cn("text-xs", statusProps.className)}>
					{statusProps.label}
				</Badge>
			)}
		</div>
	);
}

// --- UI Sub-Components ---

/**
 * Renders the search input and status filter dropdowns.
 */
function TableFilters() {
	const { state, dispatch, isStateMatchingAssignmentFilters } =
		useKorrekturTableState();
	return (
		<div className="flex flex-col sm:flex-row gap-4 mt-4">
			<div className="relative flex-1">
				<Search
					className={cn(
						"absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4",
						isStateMatchingAssignmentFilters && state.searchTerm
							? "text-blue-500"
							: "text-slate-400",
					)}
				/>
				<Input
					type="text"
					placeholder="Suchen nach ID, Klausur oder Korrektor..."
					value={state.searchTerm}
					onChange={(e) =>
						dispatch({ type: "SET_SEARCH_TERM", payload: e.target.value })
					}
					className={cn(
						"pl-10",
						isStateMatchingAssignmentFilters && state.searchTerm
							? "border-blue-300 bg-blue-50/50 focus:border-blue-500 focus:ring-blue-200"
							: "",
					)}
				/>
				{isStateMatchingAssignmentFilters && state.searchTerm && (
					<div className="absolute right-3 top-1/2 transform -translate-y-1/2">
						<Badge
							variant="secondary"
							className="text-xs bg-blue-100 text-blue-700 border-blue-300"
						>
							Vorbefüllt aus Zuweisung
						</Badge>
					</div>
				)}
			</div>

			<div className="flex items-center gap-2">
				<Filter className="h-4 w-4 text-slate-400" />
				<Select
					value={state.selectedStatus}
					onValueChange={(value) =>
						dispatch({ type: "SET_SELECTED_STATUS", payload: value })
					}
				>
					<SelectTrigger className="w-48">
						<SelectValue placeholder="Status auswählen..." />
					</SelectTrigger>
					<SelectContent>
						{statusOptions.map((option) => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
		</div>
	);
}

/**
 * Renders the panel for bulk assigning korrektors to selected klausuren.
 * This component only appears when one or more rows are selected.
 */
function BulkAssignmentPanel() {
	const {
		state,
		isStateMatchingAssignmentFilters,
		bulkKorrektorOptions,
		handleBulkAssignment,
		handleClearAllSelection,
	} = useKorrekturTableState();
	const { assignmentFilters } = useKorrekturStepAssignmentOperations();
	const [activeKorrektorSelector, setActiveKorrektorSelector] = useState<
		number | null
	>(assignmentFilters?.stepNumber || null);
	const [openKorrektorSelector, setOpenKorrektorSelector] = useState<
		number | null
	>(null);
	const [bulkSelectedKorrektor, setBulkSelectedKorrektor] = useState<string>(
		assignmentFilters?.korrektorId || "",
	);
	if (state.selectedStudentKlausurIds.size === 0) return null;

	const resetBulkAssignmentUI = () => {
		setActiveKorrektorSelector(null);
		setOpenKorrektorSelector(null);
		setBulkSelectedKorrektor("");
	};

	const handleAndClearAllSelection = () => {
		handleClearAllSelection();
		resetBulkAssignmentUI();
	};

	return (
		<div
			className={cn(
				"mt-4 p-4 border rounded-lg",
				isStateMatchingAssignmentFilters
					? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-sm"
					: "bg-blue-50 border-blue-200",
			)}
		>
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center gap-2">
					<Users className="h-4 w-4 text-blue-600" />
					<span className="text-sm font-medium text-blue-900">
						{state.selectedStudentKlausurIds.size} Klausur(en) ausgewählt
					</span>
					{isStateMatchingAssignmentFilters && (
						<Badge
							variant="secondary"
							className="text-xs bg-blue-100 text-blue-700 border-blue-300 ml-2"
						>
							Vorbefüllt aus Zuweisung
						</Badge>
					)}
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={handleAndClearAllSelection}
					className="text-blue-600 hover:text-blue-800"
				>
					<X className="h-4 w-4" />
				</Button>
			</div>

			<div className="flex gap-3 mb-4">
				{state.availableStepNumbers.slice(0, 3).map((stepNr) => (
					<div
						key={stepNr}
						className={`
									transition-all duration-500 ease-in-out
									${
										activeKorrektorSelector !== null &&
										activeKorrektorSelector !== stepNr
											? "flex-shrink-[10] opacity-30"
											: activeKorrektorSelector === stepNr
												? "flex-1"
												: "flex-1"
									}
								`}
					>
						<div className="flex flex-col gap-2">
							<SearchableSelector
								options={bulkKorrektorOptions}
								value={
									activeKorrektorSelector === stepNr
										? bulkSelectedKorrektor
										: ""
								}
								onChange={(value) => {
									setBulkSelectedKorrektor(value);
								}}
								open={openKorrektorSelector === stepNr}
								onOpenChange={(open) => {
									if (open) {
										setOpenKorrektorSelector(stepNr);
										// Safari can fail to focus the trigger button on click/tap.
										// Tie "activation" to the actual interaction that opens the popover.
										if (activeKorrektorSelector !== stepNr) {
											setActiveKorrektorSelector(stepNr);
											setBulkSelectedKorrektor("");
										}
									} else if (openKorrektorSelector === stepNr) {
										setOpenKorrektorSelector(null);
									}
								}}
								onFocus={() => {
									if (activeKorrektorSelector !== stepNr) {
										setActiveKorrektorSelector(stepNr);
										setBulkSelectedKorrektor("");
									}
								}}
								placeholder={`Korrektor*in ${stepNr}`}
								searchPlaceholder="Korrektor*in suchen..."
								emptyMessage="Keine Korrektor*innen gefunden"
								className={cn(
									"w-full",
									isStateMatchingAssignmentFilters &&
										activeKorrektorSelector === stepNr &&
										bulkSelectedKorrektor
										? "border-blue-300 bg-blue-50/50"
										: "",
								)}
							/>

							{activeKorrektorSelector === stepNr && (
								<div className="flex gap-2">
									<Button
										variant="outline"
										onClick={() => {
											resetBulkAssignmentUI();
										}}
									>
										Zurück
									</Button>
									<Button
										onClick={() => {
											if (bulkSelectedKorrektor) {
												handleBulkAssignment(stepNr, bulkSelectedKorrektor);
												resetBulkAssignmentUI();
											}
										}}
										disabled={!bulkSelectedKorrektor}
										className="bg-blue-600 hover:bg-blue-700"
									>
										Speichern
									</Button>
								</div>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

/**
 * Renders the main table body, including headers and rows.
 */
function KorrekturTableContent({
	paginatedStudentKlausuren,
}: {
	paginatedStudentKlausuren: StudentKlausurDetailedDTO[];
}) {
	const { state, dispatch, filteredStudentKlausuren } =
		useKorrekturTableState();

	const { users } = useKorrekturStepAssignmentOperations();

	// Determines if all items on the current page are selected.
	const isAllCurrentPageSelected = useMemo(() => {
		return (
			paginatedStudentKlausuren.length > 0 &&
			paginatedStudentKlausuren.every((klausur) =>
				state.selectedStudentKlausurIds.has(klausur.id),
			)
		);
	}, [paginatedStudentKlausuren, state.selectedStudentKlausurIds]);

	// Calculate the maximum number of correction steps to render table columns correctly.
	const longestKorrekturSteps = useMemo<KorrekturStep[]>(() => {
		if (state.studentKlausuren.length === 0) return [];

		const korrekturStepsArrays = state.studentKlausuren.map(
			(klausur) => klausur.korrekturSteps,
		);
		const longest = getLongestArray(korrekturStepsArrays);
		return longest.slice().sort((a, b) => a.stepNr - b.stepNr);
	}, [state.studentKlausuren]);
	return (
		<div className="overflow-auto max-h-[500px] mx-2 rounded-lg">
			<Table>
				<TableHeader className="sticky top-0 bg-white z-10">
					<TableRow>
						<TableHead className="w-12">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									if (isAllCurrentPageSelected) {
										dispatch({ type: "CLEAR_SELECTION" });
									} else {
										const pageIds = paginatedStudentKlausuren.map((k) => k.id);
										dispatch({
											type: "SELECT_ALL_CURRENT_PAGE",
											payload: pageIds,
										});
									}
								}}
								className="h-6 w-6 p-0"
							>
								{isAllCurrentPageSelected ? (
									<CheckSquare className="h-4 w-4" />
								) : (
									<Square className="h-4 w-4" />
								)}
							</Button>
						</TableHead>
						<TableHead className="w-32">Interne ID</TableHead>
						<TableHead className="w-32">Klausur</TableHead>
						{longestKorrekturSteps.map((step) => (
							<TableHead key={step.id} className="w-48">
								Korrektur {step.stepNr}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{paginatedStudentKlausuren.map((klausur) => {
						const stepMap = new Map(
							klausur.korrekturSteps.map((step) => [step.stepNr, step]),
						);

						return (
							<TableRow key={klausur.id}>
								<TableCell className="w-12">
									<Button
										variant="ghost"
										size="sm"
										onClick={() =>
											dispatch({
												type: "TOGGLE_KLAUSUR_SELECTION",
												payload: klausur.id,
											})
										}
										className="h-6 w-6 p-0"
									>
										{state.selectedStudentKlausurIds.has(klausur.id) ? (
											<CheckSquare className="h-4 w-4" />
										) : (
											<Square className="h-4 w-4" />
										)}
									</Button>
								</TableCell>
								<TableCell className="font-medium w-32">
									{klausur.internalIdentifier}
								</TableCell>
								<TableCell className="w-48">
									{state.klausurTitleMap.get(klausur.klausurId) ||
										"Unbekannte Klausur"}
								</TableCell>
								{longestKorrekturSteps.map((templateStep) => {
									const actualStep = stepMap.get(templateStep.stepNr);
									return (
										<TableCell key={templateStep.stepNr} className="w-48">
											{actualStep ? (
												<StatusCell step={actualStep} users={users} />
											) : (
												<span className="text-xs text-muted-foreground">
													Nicht verfügbar
												</span>
											)}
										</TableCell>
									);
								})}
							</TableRow>
						);
					})}
					{paginatedStudentKlausuren.length === 0 &&
						filteredStudentKlausuren.length > 0 && (
							<TableRow>
								<TableCell
									colSpan={longestKorrekturSteps.length + 3}
									className="text-center text-slate-500 py-8"
								>
									Keine Klausuren auf dieser Seite.
								</TableCell>
							</TableRow>
						)}
					{filteredStudentKlausuren.length === 0 &&
						state.studentKlausuren.length > 0 && (
							<TableRow>
								<TableCell
									colSpan={longestKorrekturSteps.length + 3}
									className="text-center text-slate-500 py-8"
								>
									Keine Klausuren entsprechen den Suchkriterien.
								</TableCell>
							</TableRow>
						)}
					{state.studentKlausuren.length === 0 && (
						<TableRow>
							<TableCell
								colSpan={longestKorrekturSteps.length + 3}
								className="text-center text-slate-500 py-8"
							>
								Keine Klausuren gefunden.
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	);
}

/**
 * Renders the pagination controls for the table.
 */
function TablePagination({
	totalPages,
	currentPage,
	itemsPerPage,
	setCurrentPage,
	setItemsPerPage,
	filteredStudentKlausuren,
}: {
	totalPages: number;
	currentPage: number;
	itemsPerPage: number;
	setCurrentPage: (page: number) => void;
	setItemsPerPage: (items: number) => void;
	filteredStudentKlausuren: StudentKlausurDetailedDTO[];
}) {
	const { state } = useKorrekturTableState();

	const currentCount = filteredStudentKlausuren.length;
	const maxCount = state.studentKlausuren.length;
	if (currentCount === 0) return null;

	return (
		<div className="flex items-center justify-between px-4 py-3">
			<div className="flex items-center gap-2">
				<span className="text-sm text-slate-600">Zeige</span>
				<Select
					value={itemsPerPage.toString()}
					onValueChange={(value) => setItemsPerPage(Number(value))}
				>
					<SelectTrigger className="w-20 h-8">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="5">5</SelectItem>
						<SelectItem value="10">10</SelectItem>
						<SelectItem value="20">20</SelectItem>
						<SelectItem value="50">50</SelectItem>
						<SelectItem value="100">100</SelectItem>
					</SelectContent>
				</Select>
				<span className="text-sm text-slate-600">
					von {currentCount}
					{currentCount !== maxCount && (
						<span className="text-slate-400 text-xs">
							{" "}
							(gefiltert von {maxCount})
						</span>
					)}
				</span>
			</div>

			{totalPages > 1 && (
				<Pagination>
					<PaginationContent>
						<PaginationItem>
							<PaginationPrevious
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (currentPage > 1) {
										setCurrentPage(currentPage - 1);
									}
								}}
								className={
									currentPage <= 1 ? "pointer-events-none opacity-50" : ""
								}
							/>
						</PaginationItem>

						{currentPage > 3 && (
							<>
								<PaginationItem>
									<PaginationLink
										href="#"
										onClick={(e) => {
											e.preventDefault();
											setCurrentPage(1);
										}}
									>
										1
									</PaginationLink>
								</PaginationItem>
								{currentPage > 4 && (
									<PaginationItem>
										<PaginationEllipsis />
									</PaginationItem>
								)}
							</>
						)}

						{Array.from({ length: totalPages }, (_, i) => i + 1)
							.filter(
								(page) =>
									page >= Math.max(1, currentPage - 2) &&
									page <= Math.min(totalPages, currentPage + 2),
							)
							.map((page) => (
								<PaginationItem key={page}>
									<PaginationLink
										href="#"
										isActive={page === currentPage}
										onClick={(e) => {
											e.preventDefault();
											setCurrentPage(page);
										}}
									>
										{page}
									</PaginationLink>
								</PaginationItem>
							))}

						{currentPage < totalPages - 2 && (
							<>
								{currentPage < totalPages - 3 && (
									<PaginationItem>
										<PaginationEllipsis />
									</PaginationItem>
								)}
								<PaginationItem>
									<PaginationLink
										href="#"
										onClick={(e) => {
											e.preventDefault();
											setCurrentPage(totalPages);
										}}
									>
										{totalPages}
									</PaginationLink>
								</PaginationItem>
							</>
						)}

						<PaginationItem>
							<PaginationNext
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (currentPage < totalPages) {
										setCurrentPage(currentPage + 1);
									}
								}}
								className={
									currentPage >= totalPages
										? "pointer-events-none opacity-50"
										: ""
								}
							/>
						</PaginationItem>
					</PaginationContent>
				</Pagination>
			)}
		</div>
	);
}

/**
 * The main container component for the table content, including the header and actions.
 */
function KorrekturStatusTableContent() {
	const { filteredStudentKlausuren } = useKorrekturTableState();
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);

	const totalPages = Math.ceil(filteredStudentKlausuren.length / itemsPerPage);

	// Slices the filtered data to get only the items for the current page.
	const paginatedStudentKlausuren = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return filteredStudentKlausuren.slice(startIndex, endIndex);
	}, [filteredStudentKlausuren, currentPage, itemsPerPage]);

	// Reset to page 1 when items per page changes
	const handleSetItemsPerPage = (items: number) => {
		setItemsPerPage(items);
		setCurrentPage(1);
	};

	return (
		<Card className="shadow-sm">
			<CardHeader className="bg-white border-b border-slate-100 pb-4">
				<CardTitle className="flex justify-between flex-row text-slate-900">
					<div className="flex items-center gap-3">
						<div className="bg-blue-100 p-2 rounded-lg">
							<FileText className="h-5 w-5 text-blue-600" />
						</div>
						<div className="flex-1">
							<h3 className="text-lg font-semibold">
								Korrektur-Status Übersicht
							</h3>
							<p className="text-sm text-slate-600 font-normal mt-1">
								Übersicht über den Status aller Korrekturschritte
							</p>
						</div>
					</div>
					{/* Status Legend */}
					<div>
						<div className="flex flex-col gap-1 text-xs items-end">
							<div className="flex items-center gap-2">
								<Badge className="bg-slate-100 text-slate-600 border-slate-200">
									Nicht begonnen
								</Badge>
							</div>
							<div className="flex items-center gap-2">
								<Badge className="bg-amber-100 text-amber-800 border-amber-200">
									In Bearbeitung
								</Badge>
							</div>
							<div className="flex items-center gap-2">
								<Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
									Abgeschlossen
								</Badge>
							</div>
						</div>
					</div>
				</CardTitle>

				<TableFilters />
				<BulkAssignmentPanel />
			</CardHeader>
			<CardContent className="p-0">
				<KorrekturTableContent
					paginatedStudentKlausuren={paginatedStudentKlausuren}
				/>
				<TablePagination
					totalPages={totalPages}
					currentPage={currentPage}
					itemsPerPage={itemsPerPage}
					setCurrentPage={setCurrentPage}
					setItemsPerPage={handleSetItemsPerPage}
					filteredStudentKlausuren={filteredStudentKlausuren}
				/>
			</CardContent>
		</Card>
	);
}

/**
 * The main export of the file. This component fetches the necessary data,
 * creates a map for klausur titles, and then wraps the entire table
 * in the state provider so all children can access the context.
 */
export function KorrekturStatusTable() {
	const {
		availableStepNumbers,
		assignmentFilters,
		studentKlausuren,
		users,
		klausuren,
		isLoading,
	} = useKorrekturStepAssignmentOperations();

	// Create mapping from klausurId to klausur title
	const klausurTitleMap = useMemo(() => {
		const map = new Map<string, string>();
		klausuren.forEach((klausur) => {
			map.set(klausur.id, klausur.title || "Unbekannte Klausur");
		});
		return map;
	}, [klausuren]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Loader2 className="h-8 w-8 animate-spin text-slate-400" />
			</div>
		);
	}

	return (
		<KorrekturTableStateProvider
			studentKlausuren={studentKlausuren}
			users={users}
			klausurTitleMap={klausurTitleMap}
			availableStepNumbers={availableStepNumbers}
			assignmentFilters={assignmentFilters}
		>
			<KorrekturStatusTableContent />
		</KorrekturTableStateProvider>
	);
}
